import { toast } from "sonner";
import { CustomButton, SearchBar } from "components/form";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PopupModal } from "components/modals/ModalBox";
import useDebounce from "common/UseDebounce";
import { toastMessage } from "utils/toastMessage";
import NotFound from "common/NotFound";
import useButtonLoader from "common/UseButtonLoader";
import Pagination from "react-js-pagination";
import useFullPageLoader from "common/UseFullPageLoader";
import { dateFormat } from "utils/dateFormat";
import { changeSupportStatusApi, supportListApi } from "services/support";
import { FaRegEye } from "react-icons/fa";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { encryptData } from "utils/dataCipher";

const SupportManagement = () => {
  // State Handler
  const [supportList, setSupportList] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const debouncedValue = useDebounce(search, 300);
  const [selectedDetail, setSelectedDetail] = useState({});
  const [statusModal, setStatusModal] = useState(false);
  const [total, setTotal] = useState(null);
  const activePage = +searchParams.get("page") || 1;
  const limit = +searchParams.get("limit") || 10;
  const ticketStatus = searchParams.get("ticketStatus") || 10;
  // Hook Handler
  const [loader, onShow, onHide] = useFullPageLoader();
  const [buttonLoader, setButtonLoader] = useButtonLoader("Yes");

  /**************Change Status Handler*********************/
  const changeStatusHandler = async () => {
    try {
      setButtonLoader(true);
      const res = await changeSupportStatusApi({
        ticketId: selectedDetail._id,
      });

      if (res.data.status === 200) {
        setSupportList((prevList) =>
          prevList.map((ticket) =>
            ticket._id === selectedDetail._id
              ? {
                ...ticket,
                ticketStatus:
                  ticket.ticketStatus === "open" ? "closed" : "open",
              }
              : ticket
          )
        );
        toastMessage(res.data.message, "success", "changeStatus");
        setStatusModal(false);
      } else {
        toastMessage(res.data.message, "error", "changeStatus");
      }
    } catch (e) {
      console.log("error:", e.error);
    } finally {
      setButtonLoader(false);
    }
  };

  /*************Reset Filter Handler*********************/
  const resetFilterHandler = () => {
    searchParams.delete("page");
    setSearch("");
    searchParams.delete("ticketStatus");
    setSearchParams(searchParams);
  };

  //*********Handel Pagination********* */
  const handlePageChange = (event) => {
    searchParams.set("page", event);
    setSearchParams(searchParams);
  };

  /**************Modal CLose*********************/
  const closeModal = () => {
    setStatusModal(false);
  };

  // support list handler
  const statusListHandler = async (
    page = activePage,
    limit = 10,
    search = debouncedValue
  ) => {
    try {
      onShow();
      const sendData = {
        page,
        limit,
      };

      if (search && search?.trim) {
        sendData.search = debouncedValue;
      }

      if (ticketStatus && ticketStatus?.trim) {
        sendData.status = ticketStatus;
      }

      const res = await supportListApi(sendData);
      if (res?.data?.status === 200) {
        setTotal(res?.data?.data?.total);
        setSupportList(res.data?.data?.tickets);
        onHide();
      } else {
        toastMessage(res?.data?.message, "error", "supportList");
      }
    } catch (e) {
      onHide();
      toast.error(e.message);
    }
  };

  useEffect(() => {
    if (Number(activePage) === 1) {
      searchParams.delete("page");
      setSearchParams(searchParams);
    }
    statusListHandler();
  }, [searchParams, debouncedValue, activePage, ticketStatus]);

  useEffect(() => {
    if (search) {
      searchParams.delete("page");
    }
  }, []);

  return (
    <>
      <div className="student_management">
        <div className="card top_card">
          <div className="dashboard_title has_child">
            <h3>Help & Support Management</h3>
            <div className="d-flex"></div>
          </div>
          <div className="button_section_wrap">
            <div className="search_wrapper">
              <SearchBar
                placeholder={"Search..."}
                onChange={(e) => {
                  setSearch(e.target.value);
                  searchParams.delete("page");
                  setSearchParams(searchParams);
                }}
                value={search}
              />
              <div className="d-flex">
                <select
                  className="input form-control"
                  onChange={(e) => {
                    if (e.target.value === "") {
                      searchParams.delete("ticketStatus");
                    } else {
                      searchParams.set("ticketStatus", e.target.value);
                    }
                    setSearchParams(searchParams);
                  }}
                  value={ticketStatus}
                >
                  <option value={""}>Select Ticket Status</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
                <CustomButton onClick={resetFilterHandler}>Reset</CustomButton>
              </div>
            </div>
          </div>
          <div className="d-flex"></div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="table_wrap">
              <table className="table custom_table">
                <thead>
                  <tr>
                    <th className="nowrap">S.No.</th>
                    <th className="nowrap">Ticket No.</th>
                    <th className="nowrap cursor-pointer">Sender Name</th>
                    <th className="nowrap">Subject </th>
                    <th className="nowrap cursor-pointer">Status</th>
                    <th className="nowrap cursor-pointer">Created At</th>
                    <th className="nowrap cursor-pointer">View</th>
                  </tr>
                </thead>
                <tbody>
                  {loader}
                  {supportList && supportList?.length > 0 ? (
                    supportList?.map((data, index) => {
                      return (
                        <tr key={++index}>
                          <td className="nowrap">{++index + (activePage - 1) * 10}</td>
                          <td className="nowrap">{data?.ticketNumber || ""}</td>
                          <td className="nowrap text-capitalize">
                            {data?.senderName || ""}
                          </td>
                          <td className="nowrap">{data?.subject || ""}</td>
                          <td className="nowrap">
                            <CustomButton
                              disabled={data?.ticketStatus === "closed"}
                              onClick={(e) => {
                                setSelectedDetail(data || "");
                                setStatusModal(true);
                              }}
                            >
                              {data?.ticketStatus || ""}
                            </CustomButton>
                          </td>

                          <td className="nowrap">
                            {dateFormat(data?.createdAt)}
                          </td>
                          <td className="nowrap">
                            <div className="icon_wrap">
                              <Link
                                to={`/support-management/chat/${data?._id}?info=${encryptData(data)}`}
                                className="icon_button"
                              >
                                {/* <FaRegEye /> */}
                                <IoChatbubbleEllipsesOutline />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <NotFound
                      cols={12}
                      msg={"Support data not found"}
                      onHide={onHide}
                    />
                  )}
                </tbody>
              </table>
            </div>
            {total > limit && (
              <div className="pagination-wrapper">
                <div className="Pagination">
                  <Pagination
                    activePage={activePage}
                    previousLabel={"previous"}
                    nextLabel={"next"}
                    itemsCountPerPage={limit}
                    totalItemsCount={total}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                    hideDisabled={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PopupModal
        show={statusModal}
        handleClose={closeModal}
        content="Are you sure you want to close of this Ticket?"
        title="Change Ticket Status"
        onShow={changeStatusHandler}
        buttonLoader={buttonLoader}
      />
    </>
  );
};

export default SupportManagement;
